import type { MarketProject } from '../types/market';
import {
  DEFAULT_PROGRESS_FILTER,
  type CompletionStatus,
  type DockingRecord,
  type ProgressFilter,
  type ProgressStatistics,
} from '../types/docking';

export function isProgressFilterDirty(filter: ProgressFilter): boolean {
  if (filter.category !== DEFAULT_PROGRESS_FILTER.category) return true;
  if (filter.keyword?.trim()) return true;
  if (filter.completion !== DEFAULT_PROGRESS_FILTER.completion) return true;
  if (filter.overdue !== DEFAULT_PROGRESS_FILTER.overdue) return true;
  return false;
}

function matchesKeyword(
  record: DockingRecord,
  project: MarketProject | undefined,
  keyword: string,
): boolean {
  const k = keyword.toLowerCase();
  const parts = [
    record.projectName,
    record.followUpPlan,
    record.summary,
    record.counterpartyOrg,
    project?.name,
    project?.id,
    project?.client,
    project?.location,
    project?.province,
    project?.department,
  ];
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(k);
}

/** 是否逾期：已填写逾期天数且 > 0 */
export function isRecordOverdue(record: DockingRecord): boolean {
  return record.overdueDays != null && record.overdueDays > 0;
}

export function isRecordNotOverdue(record: DockingRecord): boolean {
  if (record.overdueDays === 0) return true;
  const label = record.overdueLabel.trim();
  return label === '未逾期' || label === '未超期' || label === '无逾期';
}

export function filterProgressRecords(
  records: DockingRecord[],
  projectsById: Map<string, MarketProject>,
  filter: ProgressFilter,
): DockingRecord[] {
  return records.filter((record) => {
    const project = projectsById.get(record.projectId);
    if (!project) return false;
    if (filter.category !== 'all' && project.category !== filter.category) {
      return false;
    }
    if (filter.completion === 'unspecified') {
      if (record.completionStatus != null) return false;
    } else if (filter.completion !== 'all') {
      if (record.completionStatus !== filter.completion) return false;
    }
    if (filter.overdue === 'overdue') {
      if (!isRecordOverdue(record)) return false;
    } else if (filter.overdue === 'not_overdue') {
      if (!isRecordNotOverdue(record)) return false;
    } else if (filter.overdue === 'unspecified') {
      if (record.overdueDays != null || record.overdueLabel.trim()) return false;
    }
    const kw = filter.keyword?.trim();
    if (kw && !matchesKeyword(record, project, kw)) return false;
    return true;
  });
}

export function sortProgressRecords(records: DockingRecord[]): DockingRecord[] {
  return [...records].sort((a, b) => {
    // 未完成优先，其次按对接日期倒序
    const rank = (s: CompletionStatus | null) =>
      s === 'incomplete' ? 0 : s === 'completed' ? 2 : 1;
    const byStatus = rank(a.completionStatus) - rank(b.completionStatus);
    if (byStatus !== 0) return byStatus;
    return b.dockedAt.localeCompare(a.dockedAt);
  });
}

export function countProgressStatistics(
  records: DockingRecord[],
): ProgressStatistics {
  return {
    total: records.length,
    completed: records.filter((r) => r.completionStatus === 'completed').length,
    incomplete: records.filter((r) => r.completionStatus === 'incomplete')
      .length,
    overdue: records.filter((r) => isRecordOverdue(r)).length,
  };
}

export function completionStatusLabel(
  status: CompletionStatus | null | undefined,
): string {
  if (status === 'completed') return '已完成';
  if (status === 'incomplete') return '未完成';
  return '未填写';
}

export function formatOverdueDisplay(record: DockingRecord): string {
  if (record.overdueLabel.trim()) return record.overdueLabel.trim();
  if (record.overdueDays == null) return '—';
  if (record.overdueDays === 0) return '未逾期';
  return `${record.overdueDays} 天`;
}
